// The plates read in a recording (PRD-0074 slice 07, ADR-0087): one row per reading beside the
// faces — the number as read, in a mono face, the country whose format it fits, how sure the
// frames were, and what became of it: linked to the case's vehicle, waiting for an officer, or
// rejected. A collaborator confirms a waiting reading (correcting the number when the recogniser
// misread it), rejects it, or reopens a decided one. The readings and the decisions arrive as
// props; only the crops are fetched, by `Thumbnail`.
import { Car, Play } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Thumbnail } from '@/components/humint/Thumbnail'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { PlateActions } from '@/features/document-overlay/hooks/useDocumentPlates'
import type { PlateReading } from '@/services/api/video'
import { cn } from '@/utils/cn'
import { formatVideoTime } from '@/utils/video-file'

const COPY = 'documents.detail.plates'

export interface PlatesInRecordingProps {
  plates: readonly PlateReading[]
  /** Omitted for a viewer (PRD-0032 SC-036): the rows show every reading and offer nothing. */
  actions?: PlateActions
  /** "Play pass" on a reading: the player runs its span. Given only when a player is on the page. */
  onPlaySpan?: (from: number, to: number) => void
  /** Where a vehicle's page lives, when the app has one for it. */
  vehicleHref?: (vehicleId: string) => string
}

export function PlatesInRecording({
  plates,
  actions,
  onPlaySpan,
  vehicleHref,
}: PlatesInRecordingProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2" data-testid="plates-in-recording">
      {actions?.error ? (
        <p className="text-xs text-destructive" role="alert">
          {t(`${COPY}.error.${actions.error}`)}
        </p>
      ) : null}
      {!actions ? <p className="text-xs text-muted-foreground">{t(`${COPY}.readOnly`)}</p> : null}
      <ul className="flex flex-col gap-2">
        {plates.map(plate => (
          <PlateRow
            key={plate.plate_id}
            plate={plate}
            actions={actions}
            onPlaySpan={onPlaySpan}
            vehicleHref={vehicleHref}
          />
        ))}
      </ul>
    </div>
  )
}

function PlateRow({
  plate,
  actions,
  onPlaySpan,
  vehicleHref,
}: { plate: PlateReading } & Pick<
  PlatesInRecordingProps,
  'actions' | 'onPlaySpan' | 'vehicleHref'
>) {
  const { t } = useTranslation()
  const pending = actions?.pendingPlateId === plate.plate_id
  const [correcting, setCorrecting] = useState(false)
  return (
    <li
      className={cn(
        'flex flex-col gap-2 rounded-md border border-border p-2',
        plate.link_status === 'rejected' && 'opacity-70'
      )}
      data-testid="plate-row"
      data-plate-id={plate.plate_id}
      data-plate-status={plate.link_status}
    >
      <div className="flex items-center gap-3">
        <Thumbnail
          url={plate.crop_url}
          alt={t(`${COPY}.cropAlt`, { text: plate.text })}
          Icon={Car}
          className="h-8 w-24 rounded-sm border border-border"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-mono text-base tabular-nums text-foreground">{plate.text}</span>
            <span className="text-xs text-muted-foreground">
              {plate.country
                ? t(`${COPY}.format`, { country: plate.country })
                : t(`${COPY}.noFormat`)}
              {' · '}
              {t(`${COPY}.sure`, { percent: Math.round(plate.confidence * 100) })}
              {' · '}
              {t(`${COPY}.span`, {
                from: formatVideoTime(plate.t_start),
                to: formatVideoTime(plate.t_end),
              })}
            </span>
          </div>
          <PlateStatus plate={plate} vehicleHref={vehicleHref} />
        </div>
        {onPlaySpan ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={t('documents.detail.faces.playPass')}
            title={t('documents.detail.faces.playPass')}
            onClick={() => onPlaySpan(plate.t_start, plate.t_end)}
            data-testid="plate-play"
          >
            <Play aria-hidden />
          </Button>
        ) : null}
      </div>
      {actions ? (
        <PlateDecisions
          plate={plate}
          actions={actions}
          pending={pending}
          correcting={correcting}
          setCorrecting={setCorrecting}
        />
      ) : null}
    </li>
  )
}

/** What became of the reading, in words: the vehicle it is linked to, or that it waits or was rejected. */
function PlateStatus({
  plate,
  vehicleHref,
}: {
  plate: PlateReading
  vehicleHref?: (id: string) => string
}) {
  const { t } = useTranslation()
  if (plate.link_status === 'linked' && plate.vehicle) {
    const name = plate.vehicle.display_name ?? plate.vehicle.spz
    return (
      <p className="text-xs text-foreground">
        {t(`${COPY}.linkedTo`)}{' '}
        {vehicleHref ? (
          <a
            href={vehicleHref(plate.vehicle.id)}
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            {name}
          </a>
        ) : (
          <span className="font-medium">{name}</span>
        )}
      </p>
    )
  }
  return (
    <p className="text-xs text-muted-foreground">
      {t(plate.link_status === 'rejected' ? `${COPY}.rejected` : `${COPY}.waiting`)}
    </p>
  )
}

interface PlateDecisionsProps {
  plate: PlateReading
  actions: PlateActions
  pending: boolean
  correcting: boolean
  setCorrecting: (next: boolean) => void
}

/** The decisions a collaborator makes on a reading, by its state. */
function PlateDecisions({
  plate,
  actions,
  pending,
  correcting,
  setCorrecting,
}: PlateDecisionsProps) {
  const { t } = useTranslation()
  const [text, setText] = useState(plate.text)
  if (plate.link_status !== 'suggested') {
    return (
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="xs"
          disabled={pending}
          onClick={() => void actions.decide(plate.plate_id, 'reopen')}
          data-testid="plate-reopen"
        >
          {t(`${COPY}.reopen`)}
        </Button>
      </div>
    )
  }
  const confirm = (event: FormEvent) => {
    event.preventDefault()
    const typed = text.trim()
    void actions
      .decide(plate.plate_id, 'confirm', typed && typed !== plate.text ? typed : null)
      .then(saved => saved && setCorrecting(false))
  }
  return (
    <form onSubmit={confirm} className="flex flex-wrap items-center justify-end gap-2">
      {correcting ? (
        <Input
          value={text}
          onChange={event => setText(event.target.value.toUpperCase())}
          aria-label={t(`${COPY}.correctLabel`)}
          className="h-7 w-36 font-mono text-sm uppercase tabular-nums"
          maxLength={16}
          data-testid="plate-correction"
        />
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="xs"
          className="mr-auto"
          disabled={pending}
          onClick={() => setCorrecting(true)}
          data-testid="plate-correct"
        >
          {t(`${COPY}.correct`)}
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="xs"
        disabled={pending}
        onClick={() => void actions.decide(plate.plate_id, 'reject')}
        data-testid="plate-reject"
      >
        {t(`${COPY}.reject`)}
      </Button>
      <Button
        type="submit"
        size="xs"
        disabled={pending || !text.trim()}
        data-testid="plate-confirm"
      >
        {t(`${COPY}.confirm`)}
      </Button>
    </form>
  )
}
